"use client";

import clsx from "clsx";
import {
  Autocomplete,
  Button,
  Input,
  Popover,
  ListBox,
  ListBoxItem,
  Label,
  SearchField,
  Select as AriaSelect,
} from "react-aria-components";
import { useFilter } from "react-aria";
import CountryFlag from "@ui/components/languages/CountryFlag";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@ui/components/core/Tooltip";
import ChevronDown16 from "@ui/icons/16x16/chevron-down-16.svg";
import Info16 from "@ui/icons/16x16/info-16.svg";
import { X } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { FieldContainer } from "./FieldContainer";
import { ScrollFadeOverlay } from "./ScrollFadeOverlay";
import { COUNTRY_DATA } from "@mw/shared/countryData";
import {
  fieldErrorClassname,
  fieldInputVariants,
  fieldLabelClassname,
  selectContentClassname,
  selectItemClassname,
  selectTriggerClassname,
} from "./field-variants";
import { useFieldContext } from "../hooks/form-context";
import { useScrollFade } from "../hooks/useScrollFade";
import { useTranslations } from "next-intl";

export interface CountrySelectProps {
  label: string;
  name: string;
  placeholder?: string;
  required?: boolean;
  /** Whether to show the tooltip with helper text. Defaults to true. */
  showTooltip?: boolean;
}

export const CountrySelect = ({ label, name, required, showTooltip = true }: CountrySelectProps) => {
  const field = useFieldContext();
  const t = useTranslations("ClientKeys.contact_sales_page.form");
  const value = (field.state.value as string) || "";
  const [isOpen, setIsOpen] = useState(false);
  const listBoxRef = useRef<HTMLDivElement>(null);
  const { canScrollUp, canScrollDown, updateScrollState, resetScrollState, initScrollState } =
    useScrollFade(listBoxRef);

  const { contains } = useFilter({ sensitivity: "base" });

  const countries = COUNTRY_DATA;

  // Find the selected country from the full list to preserve display while searching
  const selectedCountry = useMemo(() => countries.find(c => c.value === value), [countries, value]);

  const listBoxItems = useMemo(() => countries.map(country => ({ id: country.value, ...country })), [countries]);

  const labelElement = (
    <div className='tw-flex tw-justify-between'>
      <div className='tw-flex tw-items-center tw-gap-2'>
        <Label className={fieldLabelClassname} htmlFor={name}>
          {label}
        </Label>
        {showTooltip && (
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type='button' className='tw-transition-colors hover:text-black text-light'>
                  <Info16 className='tw-h-4 tw-w-4 mb-8' />
                </button>
              </TooltipTrigger>
              <TooltipContent className='tw-max-w-96 tw-items-center tw-text-center'>
                <p className='tw-text-xs text-light'>{t("tooltips.country_hq_helper")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      {!required && <span className='tw-text-[0.75rem] tw-text-black tw-opacity-40'>{t("labels.optional")}</span>}
    </div>
  );

  const errorElement = !field.state.meta.isValid &&
    field.state.meta.isBlurred &&
    field.state.meta.errors.length > 0 && (
      <div className={fieldErrorClassname} role='alert'>
        {field.state.meta.errors[0]}
      </div>
    );

  const emptyStateElement = (
    <div className='tw-px-3 tw-py-3 tw-text-sm text-light'>{t("empty_states.no_countries_found")}</div>
  );

  const listBoxContent = (
    <ListBox
      ref={listBoxRef}
      className='tw-max-h-64 tw-overflow-auto tw-outline-none tw-pt-1'
      items={listBoxItems}
      onScroll={updateScrollState}
      renderEmptyState={() => emptyStateElement}
    >
      {item => (
        <ListBoxItem
          id={item.value}
          textValue={item.label}
          className={({ isSelected, isHovered }) =>
            clsx(selectItemClassname, "tw-rounded-lg", isSelected && "tw-font-semibold", isHovered && "bg-dark")
          }
        >
          <div className='tw-flex tw-items-center tw-gap-2 tw-overflow-hidden'>
            <CountryFlag
              countryCode={item.flag_code}
              alt={`${item.label} flag`}
              className='tw-shrink-0 tw-rounded-full'
            />
            <span className='tw-truncate'>{item.label}</span>
          </div>
        </ListBoxItem>
      )}
    </ListBox>
  );

  return (
    <FieldContainer>
      {labelElement}
      <AriaSelect
        name={name}
        value={value || null}
        isOpen={isOpen}
        onChange={key => {
          if (key !== null) {
            field.handleChange(key as string);
          }
        }}
        onOpenChange={open => {
          setIsOpen(open);
          if (open) {
            // Set initial scroll state: start at top, always scrollable with 200+ countries
            initScrollState(true);
          } else {
            resetScrollState();
            field.handleBlur();
          }
        }}
        aria-label={label}
      >
        <Button id={name} className={clsx(selectTriggerClassname, "tw-text-left")}>
          <span className='tw-flex-1 tw-truncate'>
            {selectedCountry ? (
              <span className='tw-flex tw-items-center tw-gap-2 tw-overflow-hidden'>
                <CountryFlag
                  countryCode={selectedCountry.flag_code}
                  alt={`${selectedCountry.label} flag`}
                  className='tw-shrink-0 tw-rounded-full'
                />
                <span className='tw-truncate'>{selectedCountry.label}</span>
              </span>
            ) : (
              <span className='tw-text-gray-400'>{t("placeholders.search_countries")}</span>
            )}
          </span>
          <ChevronDown16
            className={clsx("tw-ml-2 tw-h-4 tw-w-4 tw-transition-transform tw-duration-200 text-lighter", {
              "tw-rotate-180": isOpen,
            })}
          />
        </Button>
        <Popover
          className={clsx(selectContentClassname, "tw-flex tw-flex-col tw-w-[var(--trigger-width)]")}
          offset={4}
          placement='bottom'
          containerPadding={0}
        >
          <Autocomplete filter={contains}>
            <SearchField
              aria-label={t("placeholders.search_countries")}
              className='tw-px-3 tw-pt-3 tw-pb-3'
              onChange={() => setTimeout(updateScrollState, 0)}
            >
              <div
                className={clsx(
                  fieldInputVariants(),
                  "tw-flex tw-items-center tw-gap-2 tw-h-9 tw-py-2 tw-px-3 tw-text-sm",
                )}
              >
                <Input
                  placeholder={t("placeholders.search_countries")}
                  className='tw-flex-1 tw-min-w-0 tw-border-none tw-outline-none tw-bg-transparent tw-text-sm placeholder:tw-text-gray-400 [&::-webkit-search-cancel-button]:tw-hidden'
                />
                <Button className='tw-p-0.5 tw-rounded tw-text-gray-400 hover:tw-text-gray-600 hover:tw-bg-black/5 tw-transition-colors group-empty:tw-invisible'>
                  <X className='tw-size-3' aria-hidden />
                </Button>
              </div>
            </SearchField>
            <div className='tw-border-t border-default' />
            <ScrollFadeOverlay canScrollUp={canScrollUp} canScrollDown={canScrollDown}>
              {listBoxContent}
            </ScrollFadeOverlay>
          </Autocomplete>
        </Popover>
      </AriaSelect>
      {errorElement}
    </FieldContainer>
  );
};

export const FieldCountry = CountrySelect;
